// Statement-specific keyword ladders — MERGE's `WHEN MATCHED …` branches, the
// upsert tails (`ON CONFLICT …` / `ON DUPLICATE KEY …`), T-SQL's hint parens,
// and MySQL's `SHOW`.
//
// These differ from `clauseShapeSlot.ts`'s shapes in that they are recognized
// from the STATEMENT's leading keyword rather than from the clause: the clause
// machine has no state for them, so every caret in the ladder used to land in
// whatever clause the last recognized keyword happened to set (a MERGE branch
// reads as `join-on`, an upsert tail as `values`) and got that clause's answer.
//
// Same CONFIDENT-ONLY contract as its siblings: `null` unless the scan is
// clearly at the named boundary. PURE text scan — no parse, no async.

import type { CompletionPositionKind } from '@/core/engines';

import type { NextKeywordSlot } from './legalNextKeywords';
import { scanRegion, slotScanEnd, wordBefore, type ScanOptions } from './slotTextScan';

/** The MERGE branch ladder: `WHEN [NOT] MATCHED [BY …] [AND <pred>] THEN
 *  { UPDATE SET … | DELETE | INSERT … }`, repeated. */
export function classifyMergeSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const words = scan.tokens.map((t) => t.word);
  if (words[0] !== 'MERGE') return null;
  // Inside an unclosed paren the ladder is suspended: `THEN INSERT (▮` is the
  // target's COLUMN LIST, not the branch boundary, and answering it with the
  // branch keywords suppressed every column. The `ON (…)` predicate and a
  // parenthesised `AND (…)` are the same story.
  if (scan.openParenAt !== null) return null;

  const lastWhen = lastBranchWhen(words);
  if (lastWhen === -1) return null; // still in USING / ON — handled elsewhere.
  const branch = words.slice(lastWhen);
  // Read the branch's polarity from the token right after WHEN. Testing the
  // whole branch for `NOT` flipped it on any negated predicate
  // (`WHEN MATCHED AND NOT (x) ▮`).
  const matched = branch[1] !== 'NOT';

  // Everything from the branch's `THEN` onward is the action.
  const thenAt = branch.indexOf('THEN');
  if (thenAt === -1) {
    // `WHEN ▮` … `WHEN NOT MATCHED BY SOURCE ▮` — still naming the branch.
    const last = branch[branch.length - 1]!;
    if (last === 'WHEN') return 'after-merge-when';
    if (last === 'NOT') return 'after-merge-not';
    if (last === 'MATCHED') return matched ? 'after-merge-matched' : 'after-merge-not-matched';
    // `BY TARGET` / `BY SOURCE` — the side is chosen, so only THEN / AND follow.
    if (last === 'TARGET' || last === 'SOURCE') return 'after-merge-by-side';
    return null; // inside the branch's `AND <predicate>`.
  }
  const action = branch.slice(thenAt + 1);
  if (action.length === 0) {
    // `BY SOURCE` restricts the branch to rows that exist only in the target,
    // so its actions are the MATCHED set — INSERT is BY TARGET only.
    const bySource = branch.includes('SOURCE');
    return matched || bySource ? 'after-merge-then-matched' : 'after-merge-then-not-matched';
  }
  if (action.length === 1) {
    if (action[0] === 'UPDATE') return 'after-merge-update';
    if (action[0] === 'INSERT') return 'after-merge-insert';
    if (action[0] === 'DELETE') return 'after-merge-branch-end';
  }
  return null;
}

/**
 * Index of the MERGE branch's own `WHEN`, or -1. A `CASE … WHEN … END` in a
 * branch predicate has its own `WHEN`s, and taking the last one outright put
 * the ladder in the wrong branch — so CASE regions are skipped.
 */
function lastBranchWhen(words: readonly string[]): number {
  let caseDepth = 0;
  let found = -1;
  for (let i = 0; i < words.length; i += 1) {
    const w = words[i]!;
    if (w === 'CASE') caseDepth += 1;
    else if (w === 'END') caseDepth = Math.max(0, caseDepth - 1);
    else if (w === 'WHEN' && caseDepth === 0) found = i;
  }
  return found;
}

/**
 * The completed-predicate tail slot for a MERGE, or null when the statement is
 * not one. A MERGE's `ON` takes `WHEN`, and a branch's own `AND <predicate>`
 * takes `THEN` — neither takes the join continuations (WHERE / JOIN / …).
 */
export function mergePredicateTailSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart) ?? offset;
  const words = scanRegion(text, statementStart, scanEnd, opts).tokens.map((t) => t.word);
  if (words[0] !== 'MERGE') return null;
  const lastWhen = lastBranchWhen(words);
  // Before any branch → the join condition itself; `WHEN` opens the ladder.
  if (lastWhen === -1) return 'after-merge-on';
  // Inside a branch's `AND <predicate>` → its `THEN` is what follows.
  return words[lastWhen + 1] === 'NOT' ? 'after-merge-not-matched' : 'after-merge-matched';
}

/** The upsert tail of an INSERT: PG / SQLite `ON CONFLICT …`, MySQL
 *  `ON DUPLICATE KEY UPDATE …`. */
export function classifyUpsertSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const words = scan.tokens.map((t) => t.word);
  // MySQL's `REPLACE` is deliberately excluded — it has no upsert tail.
  if (words[0] !== 'INSERT') return null;
  const last = scan.tokens[scan.tokens.length - 1];
  const lastIsTail = !!last && last.start + last.word.length === scan.lastCodeEnd;

  // `… ON CONFLICT (cols) ▮` — the closed paren must be the CONFLICT TARGET,
  // not just any trailing paren. Without the check, the call in
  // `… DO UPDATE SET a = COALESCE(x, 0) ▮` matched and re-offered `DO UPDATE`.
  if (scan.lastCodeChar === ')' && scan.parenDepth === 0 && words.includes('CONFLICT')) {
    const openAt = scanRegion(text, statementStart, scan.lastCodeEnd - 1, opts).openParenAt;
    if (openAt !== null && wordBefore(text, openAt, statementStart) === 'CONFLICT') {
      return 'after-conflict-target';
    }
    return null;
  }
  if (!lastIsTail) return null;

  switch (last.word) {
    // `… VALUES (1) ON ▮` — only the dialect's upsert keyword can follow. A
    // JOIN inside an `INSERT … SELECT` has its own `ON`, so refuse when one is
    // still open. (`SET` is MySQL's alternative to a VALUES list.)
    case 'ON': {
      const hasRowSource =
        words.includes('VALUES') || words.includes('SELECT') || words.includes('SET');
      return hasRowSource && !hasOpenJoin(words) ? 'after-insert-on' : null;
    }
    case 'CONFLICT':
      // A relation or column literally named `conflict` is not the upsert
      // keyword — the same trap the `DUPLICATE KEY` arm below already avoids.
      return wordBefore(text, last.start, statementStart) === 'ON' ? 'after-on-conflict' : null;
    case 'DO':
      return words.includes('CONFLICT') ? 'after-conflict-do' : null;
    // MySQL `ON DUPLICATE KEY ▮`.
    case 'KEY':
      return wordBefore(text, last.start, statementStart) === 'DUPLICATE'
        ? 'after-duplicate-key'
        : null;
    default:
      return null;
  }
}

/** True when a `JOIN` appears after the row source, so the trailing `ON` is the
 *  join's rather than an upsert's. */
function hasOpenJoin(words: readonly string[]): boolean {
  const source = Math.max(words.lastIndexOf('VALUES'), words.lastIndexOf('SELECT'));
  // MySQL's `STRAIGHT_JOIN` is one token, so a bare `=== 'JOIN'` test misses it.
  return words.some((w, i) => (w === 'JOIN' || w === 'STRAIGHT_JOIN') && i > source);
}

/** MySQL's `SHOW ▮` — a fixed vocabulary of things to show, never a relation.
 *
 *  Not engine-gated here: the dialect gate lives in the DATA. PG also has a
 *  `SHOW` (it takes a configuration parameter, which is not a keyword set we
 *  can enumerate), and its empty list makes the slot decline so the normal
 *  emission serves that caret. Same for the hint parens below. */
export function classifyShowSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const words = scanRegion(text, statementStart, scanEnd, opts).tokens.map((t) => t.word);
  if (words.length !== 1 || words[0] !== 'SHOW') return null;
  return 'after-show';
}

/**
 * Small vocabularies nothing modelled: `COLLATE ▮`, `TABLESAMPLE ▮`, T-SQL's
 * `CREATE TRIGGER n ON t ▮`, and `EXTRACT(▮`.
 *
 * A bare `SET ▮` session statement is deliberately NOT here. The session
 * settings would be useful (`SET NOCOUNT ON` is T-SQL boilerplate), but the
 * same caret is a local-variable assignment inside every dialect's procedural
 * code (`SET v_total = 0`), and a keyword-only slot would hide the locals that
 * belong there. Serving it needs an emitter that ADDS to the list rather than
 * replacing it.
 */
export function classifyVocabularySlot(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  opts: ScanOptions,
): NextKeywordSlot | null {
  // `EXTRACT(▮` puts the caret immediately after the paren, which the shared
  // boundary helper refuses (neither whitespace nor a partial word).
  const scanEnd =
    slotScanEnd(text, offset, statementStart) ??
    (text[Math.min(offset, text.length) - 1] === '(' ? Math.min(offset, text.length) : null);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const words = scan.tokens.map((t) => t.word);
  // Constructs whose operand is a STRING LITERAL are checked before the
  // bareword-tail guard below: `INTERVAL '1' ▮` and `LIKE 'a%' ▮` end in a
  // literal, so their last TOKEN is the keyword two positions back.
  if (isIntervalValueTail(words, scan.lastCodeChar)) return 'after-interval-value';
  if (scan.lastCodeChar === "'" && words[words.length - 1] === 'LIKE') {
    return 'after-like-pattern';
  }

  // `WITHIN GROUP (▮` — an ordered-set aggregate's sort specification, whose
  // only legal token is `ORDER BY`.
  if (scan.openParenAt !== null) {
    const intro = wordBefore(text, scan.openParenAt, statementStart);
    const inner = scanRegion(text, scan.openParenAt + 1, scanEnd, opts);
    if (intro === 'GROUP' && inner.tokens.length === 0) {
      const beforeGroup = scanRegion(text, statementStart, scan.openParenAt, opts).tokens;
      if (beforeGroup[beforeGroup.length - 2]?.word === 'WITHIN') return 'at-within-group';
    }
  }
  // `EXTRACT(▮ FROM …)` — the datetime field, at the head of the call.
  if (scan.openParenAt !== null) {
    const inner = scanRegion(text, scan.openParenAt + 1, scanEnd, opts);
    const callee = scan.tokens[scan.tokens.length - 1];
    if (
      inner.tokens.length === 0 &&
      wordBefore(text, scan.openParenAt, statementStart) === 'EXTRACT' &&
      // `my_schema.extract(▮` is a user function whose name happens to match.
      !!callee &&
      !isQualifiedWord(text, callee.start, statementStart)
    ) {
      return 'at-extract-field';
    }
  }

  const last = scan.tokens[scan.tokens.length - 1];
  if (!last || last.start + last.word.length !== scan.lastCodeEnd) return null;

  // The construct keywords must be BARE — a qualified reference (`t.collate`)
  // is a column whose name happens to match, the same trap the keyword heads
  // and aggregate constructs hit. (The trigger check below is exempt: its
  // trailing word is a table name, which is qualified all the time.)
  const bare = !isQualifiedWord(text, last.start, statementStart);
  // `… COLLATE ▮` — a collation NAME follows, never a keyword or a column.
  // Gated to expression positions: COLLATE is not reserved in Oracle, so
  // `FROM orders collate ▮` is a legal table alias, not the operator.
  if (bare && last.word === 'COLLATE' && COLLATE_POSITION_KINDS.has(positionKind)) {
    return 'after-collate';
  }
  // `FROM t TABLESAMPLE ▮` — a sampling method. Without this the caret read as
  // a completed relation and answered WHERE / JOIN / …, none of it legal.
  if (bare && last.word === 'TABLESAMPLE' && positionKind === 'from') {
    return 'after-tablesample';
  }
  // `ALTER TABLE t ALTER COLUMN <name> ▮` — PG chooses the edit with a keyword.
  if (isPgAlterColumnTail(words)) return 'after-alter-column-name';
  // `x SIMILAR ▮` — only `TO` completes the operator.
  if (bare && last.word === 'SIMILAR') return 'after-similar';
  // `<ts> AT ▮` — `AT` read as a column alias left this a dead end.
  if (bare && last.word === 'AT') return 'after-at';
  // T-SQL's `CREATE TRIGGER <name> ON <target> ▮`.
  if (isTsqlTriggerTarget(words)) return 'after-tsql-trigger-target';
  return null;
}

/** `CREATE TRIGGER <name> ON <target> ▮` — the timing/event ladder's head in
 *  T-SQL, whose shape the PG-style recognizer does not model. */
function isTsqlTriggerTarget(words: readonly string[]): boolean {
  if (words[0] !== 'CREATE') return false;
  const triggerAt = words.indexOf('TRIGGER');
  if (triggerAt === -1) return false;
  const onAt = words.indexOf('ON', triggerAt);
  if (onAt === -1) return false;
  // Exactly the TARGET NAME after `ON` — at most `db.schema.table`. Anything
  // more means the ladder has moved on (`… WITH EXECUTE AS ▮` takes `AS`, and a
  // written timing word takes an event).
  const tail = words.slice(onAt + 1);
  if (tail.length === 0 || tail.length > 3) return false;
  return !tail.some((w) => TRIGGER_TAIL_KEYWORDS.has(w));
}

/** Positions where a `COLLATE` operator can appear. Not `'from'`: COLLATE is
 *  unreserved in Oracle, so a table or alias may legally be called that. */
const COLLATE_POSITION_KINDS: ReadonlySet<CompletionPositionKind> =
  new Set<CompletionPositionKind>(['select-list', 'where', 'having', 'order-by', 'group-by']);

/** `INTERVAL <value> ▮` — the value may be a quoted literal or a bare number,
 *  so the tell is the word two tokens back plus a completed operand. */
function isIntervalValueTail(words: readonly string[], lastCodeChar: string): boolean {
  // `INTERVAL '1' ▮` — the literal is not a token, so `INTERVAL` is still the
  // last WORD; the closing quote is what marks the value as written.
  if (lastCodeChar === "'") return words[words.length - 1] === 'INTERVAL';
  // `INTERVAL 1 ▮` — a bare number IS a token.
  return /[0-9]/.test(lastCodeChar) && words[words.length - 2] === 'INTERVAL';
}

/** `ALTER TABLE t ALTER [COLUMN] <name> ▮` — a complete column name written. */
function isPgAlterColumnTail(words: readonly string[]): boolean {
  if (words[0] !== 'ALTER' || words[1] !== 'TABLE') return false;
  let i = words.length - 1;
  if (i < 3) return false;
  // A trailing `COLUMN` means the NAME is still missing — that caret takes the
  // table's columns, not the edit keywords.
  if (words[i] === 'COLUMN') return false;
  i -= 1; // the column name
  if (words[i] === 'COLUMN') i -= 1;
  return words[i] === 'ALTER';
}

/** The first non-whitespace char before the word that precedes `openAt`. */
function charBeforeWord(text: string, openAt: number, statementStart: number): string {
  let i = openAt;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  while (i > statementStart && /[A-Za-z0-9_]/.test(text[i - 1] ?? '')) i -= 1;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return i > statementStart ? (text[i - 1] ?? '') : '';
}

/** Words that mean the T-SQL trigger ladder has moved past its target. */
const TRIGGER_TAIL_KEYWORDS: ReadonlySet<string> = new Set([
  'AFTER', 'FOR', 'INSTEAD', 'WITH', 'AS', 'NOT', 'EXECUTE',
]);

/** T-SQL hint parens: a table hint list (`FROM t WITH (▮`) and a query hint
 *  list (`OPTION (▮`). Both sit in a `from` clause and used to be answered with
 *  the relation list, of which nothing is legal inside them. Dialect-gated by
 *  the data — the other engines have no such syntax and no keywords, so the
 *  slot declines there. */
export function classifyHintParenSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd =
    slotScanEnd(text, offset, statementStart) ??
    (text[Math.min(offset, text.length) - 1] === '(' ? Math.min(offset, text.length) : null);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  const intro = wordBefore(text, openAt, statementStart);
  if (intro !== 'WITH' && intro !== 'OPTION') return null;
  // `OPENJSON(x) WITH (col type 'path')` — a table-valued function's schema
  // declaration, not a lock-hint list. The tell is the `)` that closes the
  // function call right before the `WITH`.
  if (intro === 'WITH' && charBeforeWord(text, openAt, statementStart) === ')') return null;
  // Only at the head of the list, or right after a comma — a hint already being
  // typed is a partial word the caller filters on.
  const inside = scanRegion(text, openAt + 1, scanEnd, opts);
  if (inside.tokens.length > 0 && inside.lastCodeChar !== ',') return null;
  return intro === 'OPTION' ? 'at-query-hint' : 'at-table-hint';
}

// ─── statement heads ─────────────────────────────────────────────────────────

/** Leading keywords whose next token is a fixed vocabulary, mapped to the slot
 *  that carries it. Each of these carets used to lead with the schema flood,
 *  burying the one word that belongs there 130-190 items down. */
const STATEMENT_HEAD_SLOTS: ReadonlyMap<string, NextKeywordSlot> = new Map([
  ['CREATE', 'after-create'],
  ['DROP', 'after-drop'],
  ['ALTER', 'after-alter'],
  ['EXPLAIN', 'after-explain'],
  ['DELETE', 'after-delete'],
  // `BEGIN` is deliberately absent: it opens a PL/SQL block, a T-SQL batch and
  // a MySQL routine body far more often than a transaction, and answering
  // those with `TRANSACTION` would hide every statement starter. `DESC` is
  // absent for the same reason — MySQL's `DESC <table>` names a relation.
  ['START', 'after-transaction-verb'],
  ['COMMIT', 'after-commit-verb'],
  ['ROLLBACK', 'after-rollback-verb'],
] as ReadonlyArray<readonly [string, NextKeywordSlot]>);

/** Modifiers that may sit between the leading verb and the object kind, so the
 *  slot still applies (`CREATE OR REPLACE ▮`, `DROP TABLE IF EXISTS ▮` is NOT
 *  one — that one already has its object kind). */
const HEAD_MODIFIERS: ReadonlySet<string> = new Set(['OR', 'REPLACE', 'GLOBAL', 'TEMPORARY', 'TEMP']);

/**
 * The statement's leading keyword, when what follows it is a fixed vocabulary
 * rather than a name. Declines once the object kind has been written, so
 * `CREATE TABLE ▮` keeps its normal (naming) behavior.
 */
export function classifyStatementHeadSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const words = scan.tokens.map((t) => t.word);
  if (words.length === 0) return null;
  const slot = STATEMENT_HEAD_SLOTS.get(words[0]!);
  if (slot === undefined) return null;
  // Everything after the verb must still be a modifier — one real word means
  // the object kind (or the DELETE's FROM) is already there.
  if (words.slice(1).some((w) => !HEAD_MODIFIERS.has(w))) return null;
  // `CREATE OR ▮` still needs REPLACE / ALTER — an object kind cannot follow it.
  if (words[words.length - 1] === 'OR') return null;
  // The last thing written must be a word, not punctuation.
  const last = scan.tokens[scan.tokens.length - 1]!;
  if (last.start + last.word.length !== scan.lastCodeEnd) return null;
  return slot;
}

/**
 * `GRANT … ON <object> ▮` — the grantee clause (`TO` / `FROM`) is all that can
 * follow. Without this the caret looked like a completed FROM relation and got
 * the query continuations (WHERE / JOIN / GROUP / …), none of them legal in a
 * privilege statement.
 */
export function classifyGrantObjectSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const words = scan.tokens.map((t) => t.word);
  if (words[0] !== 'GRANT' && words[0] !== 'REVOKE') return null;
  const onAt = words.indexOf('ON');
  // The object name must be written, and the grantee clause not yet.
  if (onAt === -1 || words.length <= onAt + 1) return null;
  if (words.includes('TO') || words.slice(onAt).includes('FROM')) return null;
  return 'after-grant-object';
}

/** True when the statement is a privilege statement, whose `ON` names an
 *  object rather than opening a join predicate. */
export function isGrantStatement(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): boolean {
  const scanEnd = slotScanEnd(text, offset, statementStart) ?? offset;
  const first = scanRegion(text, statementStart, scanEnd, opts).tokens[0]?.word;
  return first === 'GRANT' || first === 'REVOKE';
}

/** `COMMENT ON ▮` — an object KIND follows, not an object name. */
export function classifyCommentOnSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const words = scanRegion(text, statementStart, scanEnd, opts).tokens.map((t) => t.word);
  return words.length === 2 && words[0] === 'COMMENT' && words[1] === 'ON'
    ? 'after-comment-on'
    : null;
}

// ─── post-aggregate constructs and the named-window clause ───────────────────

/**
 * `WITHIN ▮` (ordered-set aggregates), `FILTER (▮` (aggregate filters),
 * `GROUP BY GROUPING ▮`, and `WINDOW w AS (▮`. Each takes exactly one
 * construct, and none of them was known to the engine at all.
 */
export function classifyAggregateConstructSlot(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const openParenCaret =
    text[Math.min(offset, text.length) - 1] === '(' ? Math.min(offset, text.length) : null;
  const scanEnd = slotScanEnd(text, offset, statementStart) ?? openParenCaret;
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);

  // Inside a paren whose introducer names the construct.
  if (scan.openParenAt !== null) {
    const inside = scanRegion(text, scan.openParenAt + 1, scanEnd, opts);
    if (inside.tokens.length === 0) {
      const intro = wordBefore(text, scan.openParenAt, statementStart);
      if (intro === 'FILTER') return 'at-filter-clause';
      // `WINDOW w AS (▮` — the same body as `OVER (▮`.
      if (intro === 'AS' && namesAWindow(text, scan.openParenAt, statementStart, opts)) {
        return 'at-named-window-spec';
      }
    }
    return null;
  }

  const last = scan.tokens[scan.tokens.length - 1];
  if (!last || last.start + last.word.length !== scan.lastCodeEnd) return null;
  // A qualified reference (`t.within`, `t.grouping`) is a column whose name
  // happens to match — same trap the keyword heads hit.
  if (isQualifiedWord(text, last.start, statementStart)) return null;
  // `WITHIN` only follows an ordered-set aggregate, so it must sit in a
  // projection or a predicate; `GROUPING SETS` only appears in a GROUP BY.
  if (last.word === 'WITHIN' && AGGREGATE_POSITION_KINDS.has(positionKind)) return 'after-within';
  if (last.word === 'GROUPING' && positionKind === 'group-by') return 'after-grouping';
  return null;
}

/** Positions where an aggregate — and therefore its `WITHIN GROUP` tail — can
 *  appear. */
const AGGREGATE_POSITION_KINDS: ReadonlySet<CompletionPositionKind> =
  new Set<CompletionPositionKind>(['select-list', 'having', 'order-by', 'where']);

/** True when the word starting at `wordStart` is the tail of a dotted
 *  reference rather than a bare keyword. */
function isQualifiedWord(text: string, wordStart: number, lowBound: number): boolean {
  let i = wordStart;
  while (i > lowBound && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return i > lowBound && text[i - 1] === '.';
}

/** True when the `AS (` at `openAt` belongs to a `WINDOW <name> AS (…)` clause
 *  rather than a CTE or a column alias. */
function namesAWindow(
  text: string,
  openAt: number,
  statementStart: number,
  opts: ScanOptions,
): boolean {
  const words = scanRegion(text, statementStart, openAt, opts).tokens.map((t) => t.word);
  const windowAt = words.lastIndexOf('WINDOW');
  if (windowAt === -1) return false;
  // `WINDOW w AS (` — exactly the name between the keyword and the `AS`.
  return words.length - windowAt === 3 && words[words.length - 1] === 'AS';
}
